/**
 * @fileoverview RequirePermission Component
 * @description Conditionally renders children based on the user's module permissions.
 *              Use this to gate action buttons (Create, Edit, Delete) within modules.
 * @module components/auth/RequirePermission
 *
 * @example
 *   <RequirePermission module="test_calendar" operation="write">
 *     <Button onClick={handleCreate}>Create Event</Button>
 *   </RequirePermission>
 *
 *   <RequirePermission module="test_calendar" operation="delete" fallback={<Tooltip title="No permission"><span><Button disabled>Delete</Button></span></Tooltip>}>
 *     <Button onClick={handleDelete}>Delete</Button>
 *   </RequirePermission>
 */

import React from 'react';
import { useAuth } from '@/context';
import { appInsights} from '@/telemetry';

/**
 * RequirePermission — renders children only if the user has the required permission.
 * @param {Object} props
 * @param {string} props.module - Module name (e.g., 'test_calendar')
 * @param {string} [props.operation='read'] - Operation (read, write, delete, manage)
 * @param {string} [props.role] - Alternatively, check for a specific role
 * @param {React.ReactNode} [props.fallback=null] - What to render if permission is denied
 * @param {React.ReactNode} props.children - Content to render if permitted
 */
export const RequirePermission = ({
  module: moduleName,
  operation = 'read',
  role,
  fallback = null,
  children,
}) => {
  const { hasModuleAccess, hasRole, isAdmin } = useAuth();

  // Admin sees everything
  if (isAdmin) return <>{children}</>;

  // Check role if specified
  if (role && !hasRole(role)) return <>{fallback}</>;

  // Check module access if specified
  if (moduleName && !hasModuleAccess(moduleName, operation)) return <>{fallback}</>;

  appInsights.trackEvent({ 
    name: 'PermissionGranted', 
    properties: { 
      module: moduleName, 
      operation:operation, 
      role:role 
    }
  });

  return <>{children}</>;
};

export default RequirePermission;
